/**
 * mediaLibrary — pick-target + kind-filter state for the always-present Media
 * panel. Module singleton (same idiom as panelDock / canvasZoom): the panel and
 * each media widget's "choose from library" coordinate through it without a
 * provider.
 *
 * The panel is ALWAYS mounted (like the Add pane) — there is no open/close, only
 * a collapse chevron on the shell. Two modes, distinguished only by `pickTarget`:
 *   • browse — the default; a viewer/manager over all assets with a free kind
 *     filter (all / image / video / audio).
 *   • pick   — armed by a widget's "choose from library"; the kind filter is
 *     preset + locked, a banner shows the armed target, selecting a tile binds
 *     it. After binding the panel DISARMS but stays put.
 */
import { useSyncExternalStore } from "react";

import { panelDock } from "./panelDock.js";

export type MediaKind = "image" | "video" | "audio";
/** The active browse filter — a single kind, or "all". */
export type FilterKind = MediaKind | "all";

export interface PickTarget {
  readonly gridId: string;
  readonly itemId: string;
  /** Restrict the picker to this media type (defaults to image). */
  readonly kind?: MediaKind;
}

interface State {
  readonly pickTarget: PickTarget | null;
  /** Active kind filter. In pick mode it's forced to the target's kind (locked);
   *  in browse mode the user drives it. */
  readonly kindFilter: FilterKind;
  /** Bumped whenever the panel should focus/uncollapse (arming from a widget).
   *  The shell watches this. */
  readonly focusSeq: number;
}

let state: State = { pickTarget: null, kindFilter: "all", focusSeq: 0 };
const listeners = new Set<() => void>();

function set(next: State): void {
  state = next;
  for (const l of listeners) l();
}

/** Bring the (always-mounted) Media panel forward — raise it standalone, or
 *  switch to its tab if it's docked in the group. */
function focusPanel(): void {
  panelDock.bringToFront("media");
  if (panelDock.get().members.includes("media"))
    panelDock.setActiveTab("media");
}

/** Arm pick mode for a specific widget; the filter locks to the target kind and
 *  selecting a tile binds to it. Focuses/uncollapses the always-present panel. */
export function openMediaLibraryForPick(target: PickTarget): void {
  set({
    ...state,
    pickTarget: target,
    kindFilter: target.kind ?? "image",
    focusSeq: state.focusSeq + 1,
  });
  focusPanel();
}

/** Browse-mode filter control. Ignored while armed (the filter is locked to the
 *  pick target's kind). */
export function setKindFilter(kind: FilterKind): void {
  if (state.pickTarget) return; // locked while armed
  if (kind !== state.kindFilter) set({ ...state, kindFilter: kind });
}

/** Leave pick mode (post-bind, or Cancel/Esc). Resets the filter to "all". */
export function disarmPick(): void {
  if (!state.pickTarget) return;
  set({ ...state, pickTarget: null, kindFilter: "all" });
}

export function useMediaLibrary(): State {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
    () => state,
  );
}
